import { useEffect, useRef } from 'react';
import { Lock } from 'lucide-react';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { MessageBubble } from './MessageBubble';
import { useSession, useSessionMessages } from '@/api/queries';
import { useAuth } from '@/hooks/useAuth';
import { relativeTime } from '@/lib/format';

interface SessionDrawerProps {
  sessionId: string;
  onClose: () => void;
}

function MessageSkeleton() {
  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex justify-end">
        <div className="h-9 w-48 animate-pulse rounded-2xl rounded-br-sm bg-muted" />
      </div>
      <div className="flex justify-start">
        <div className="h-12 w-64 animate-pulse rounded-2xl rounded-bl-sm bg-muted" />
      </div>
      <div className="flex justify-end">
        <div className="h-9 w-32 animate-pulse rounded-2xl rounded-br-sm bg-muted" />
      </div>
    </div>
  );
}

export function SessionDrawer({ sessionId, onClose }: SessionDrawerProps) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const { data: session } = useSession(sessionId);
  const { data: messagesData, isLoading: messagesLoading } = useSessionMessages(
    isAdmin ? sessionId : undefined,
  );

  const messages = messagesData?.messages;
  // Build slack_file_id → gdrive_url lookup for URL replacement in MessageBubble
  const fileMap = Object.fromEntries(
    (messagesData?.files ?? []).map((f) => [f.slack_file_id, f.gdrive_url]),
  );

  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (messages && messages.length > 0) {
      bottomRef.current?.scrollIntoView({ behavior: 'instant' });
    }
  }, [messages]);

  return (
    <Sheet open onOpenChange={(open) => { if (!open) onClose(); }}>
      <SheetContent>
        {/* Header */}
        <div className="sticky top-0 z-10 border-b border-border bg-card px-5 pb-3 pt-5 pr-10">
          <h2 className="text-base font-semibold">
            {session?.agent_name ?? '—'}
            <span className="mx-2 text-muted-foreground font-normal">↔</span>
            {session?.human_name ?? '—'}
          </h2>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <Badge variant={session?.status === 'resolved' ? 'secondary' : 'outline'}>
              {session?.status ?? '…'}
            </Badge>
            {session?.initiated_by_agent && (
              <Badge variant="secondary">agent-initiated</Badge>
            )}
          </div>
          {session && (
            <p className="mt-2 text-[11px] text-muted-foreground font-mono">
              {session.channel_id} · {session.thread_ts} · updated {relativeTime(session.updated_at)}
            </p>
          )}
          {session?.agent_context && Object.keys(session.agent_context).length > 0 && (
            <p className="mt-1 truncate text-[11px] text-muted-foreground">
              ctx: {JSON.stringify(session.agent_context)}
            </p>
          )}
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto scrollbar-thin px-4 py-4">
          {!isAdmin ? (
            <div className="flex h-full flex-col items-center justify-center gap-2 text-muted-foreground">
              <Lock className="h-6 w-6" />
              <p className="text-sm">Message content requires admin access</p>
            </div>
          ) : messagesLoading ? (
            <MessageSkeleton />
          ) : !messages || messages.length === 0 ? (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              No messages recorded
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {messages.map((msg) => (
                <MessageBubble
                  key={msg.id}
                  message={msg}
                  agentName={session?.agent_name ?? null}
                  fileMap={fileMap}
                />
              ))}
              <div ref={bottomRef} />
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
